import {
  updateIsError as updateProfileOrderError,
  updateIsFetching as updateProfileOrderFetching,
  updateProfileOrders,
} from "../../../reducers/order/profileOrders";
import {
  updateIsError as updateOrdersListPagesError,
  updateIsFetching as updateOrdersListPagesFetching,
  updateOrdersList as updateOrdersListPages,
} from "../../../reducers/order/orders";
import { accAPI } from "../..";
import { OrderListQueryParams, OrderListRTKQueryParams, OrderResponse } from "./customerOrderListAPI.types";

const getQueryParamsString = ({
  pageNum,
  pageSize,
  period,
  sortValue,
  sortOrder,
  searchBy,
  locale,
  filterOrderByVcs,
  filterOrderBySourceType,
  customerIds,
  confidentialUserText,
  businessNature,
  requestWithSponsor,
}: OrderListQueryParams) => {
  const parsedParams = new URLSearchParams({
    PageNum: `${pageNum}`,
    PageSize: `${pageSize}`,
    Period: `${period}`,
    SortValue: `${sortValue}`,
    SortOrder: `${sortOrder}`,
  });

  // optional fields
  if (searchBy !== void 0 && searchBy.length > 0) {
    parsedParams.append("SearchBy", searchBy);
  }

  if (locale !== void 0) {
    parsedParams.append("Locale", `${locale}`);
  }

  if (customerIds !== void 0) {
    parsedParams.append("CustomerIDs", `${customerIds}`);
  }

  if (filterOrderByVcs !== void 0) {
    parsedParams.append("FilterOrderByVcs", `${filterOrderByVcs}`);
  }

  if (filterOrderBySourceType !== void 0) {
    parsedParams.append("FilterOrderBySourceType", `${filterOrderBySourceType}`);
  }

  if (confidentialUserText !== void 0 && confidentialUserText.length > 0) {
    parsedParams.append("ConfidentialUserText", confidentialUserText);
  }

  if (businessNature !== void 0) {
    parsedParams.append("BusinessNature", businessNature);
  }

  if (requestWithSponsor === true) {
    parsedParams.append("RequestWithSponsor", "true");
  }

  return parsedParams.toString();
};

const getURL = ({ affAbo, ...queryParams }: OrderListRTKQueryParams) =>
  `/customer-v3/api/${affAbo}/orders?${getQueryParamsString(queryParams)}`;

export const customerOrderListAPI = accAPI.injectEndpoints({
  endpoints: (build) => ({
    getOrderList: build.query<OrderResponse, OrderListRTKQueryParams>({
      query: (params) => getURL(params),
    }),
    // query with profileOrders reducer side-effect
    getProfileOrders: build.query<OrderResponse, OrderListRTKQueryParams>({
      query: (params) => getURL(params),
      async onQueryStarted(_args, { dispatch, queryFulfilled, requestId }) {
        try {
          dispatch(updateProfileOrderFetching({ requestId, isFetching: true }));
          const { data } = await queryFulfilled;
          dispatch(updateProfileOrders({ requestId, data }));
        } catch (error) {
          dispatch(updateProfileOrderError({ requestId, isError: true }));
        }
      },
    }),
    // query with orders reducer side-effect
    getOrderListPages: build.query<OrderResponse, OrderListRTKQueryParams>({
      query: (params) => getURL(params),
      extraOptions: {
        maxRetries: 3,
        backoff: (attempt) => new Promise((resolve) => setTimeout(resolve, attempt * 1000)),
      },
      async onQueryStarted(_args, { dispatch, queryFulfilled, requestId }) {
        try {
          dispatch(updateOrdersListPagesFetching({ requestId, isFetching: true }));
          const { data } = await queryFulfilled;
          dispatch(updateOrdersListPages({ requestId, data }));
        } catch (error) {
          dispatch(updateOrdersListPagesError({ requestId, isError: true }));
        }
      },
    }),
  }),
});

export const {
  useGetOrderListQuery,
  useLazyGetOrderListQuery,
  useGetProfileOrdersQuery,
  useLazyGetProfileOrdersQuery,
  useLazyGetOrderListPagesQuery,
} = customerOrderListAPI;
